<template>
	<nav
		class="lg-context-nav"
		aria-label="Secondary"
		:class="{ expandedContextNav: expandedMenu }"
	>
		<template v-for="route in secondaryNavigationList">
			<router-link
				v-if="!route.hidden"
				:to="pathRelativeLevel + route.path"
				:key="route.name"
			>
				{{ route.name }}
			</router-link>
		</template>
	</nav>
</template>

<script>
import { mapState } from 'vuex';

/* Secondary Horizontal Tab Navigation Component */
export default {
	name: 'LgContextNav-SecondaryNav',
	data() {
		return {
			name: String,
			currentPath: String,
			pathRelativeLevel: {
				type: String,
				default: '',
			},
			secondaryNavigationList: [],
		};
	},
	computed: {
		...mapState({
			expandedMenu: (state) => state.menus.expandedMenu,
			hasFambFeature: (state) => state.companyProfile.fambFeatureFlagIndicator,
			showPermissionsTab: (state) =>
				state.profileSwitcher.mainProfileSelector.showPermissionsTab,
			userInPilot: (state) => state.profile.isPilotUser,
		}),
		secondaryNavigationRoutes: function getNavigationRoutes() {
			return {
				'client-connect': [
					{
						name: 'Overview',
						path: 'overview',
					},
					{
						name: 'Leads',
						path: 'leads',
					},
					{
						name: 'History',
						path: 'history',
					},
					{
						name: 'Contacts',
						path: 'contacts',
					},
					{
						name: 'Import',
						path: 'import',
					},
				],
				'client-connect-v2': [
					{
						name: 'Alerts',
						path: 'alerts',
						hidden: this.userInPilot,
					},
					{
						name: 'Contacts',
						path: 'contacts',
						hidden: this.userInPilot,
					},

					{
						name: 'Drip Campaigns',
						path: 'drip-campaigns',
						hidden: this.userInPilot,
					},
				],
				'campaign-editor-home': [
					{
						name: 'Overview',
						path: 'campaign-editor-overview',
					},
					{
						name: 'All Campaigns',
						path: 'campaign-editor-all-campaigns',
					},
					{
						name: 'Popular',
						path: 'campaign-editor-popular',
					},
					{
						name: 'Recommended',
						path: 'campaign-editor-recommended',
					},
				],
				'marketing-calendar': [
					{
						name: 'Suggested',
						path: 'suggested',
					},
					{
						name: 'Scheduled',
						path: 'scheduled',
					},
				],
				'marketing-resources': [
					{
						name: 'Overview',
						path: 'marketing-overview',
					},
					{
						name: 'Quick Talks',
						path: 'quick-talks',
					},
				],
				'marketing-resources-details':
					// has the same children as marketing-resources
					[
						{
							name: 'Overview',
							path: '/marketing-resources/marketing-overview',
						},
						{
							name: 'Quick Talks',
							path: '/marketing-resources/quick-talks',
						},
					],
				'preference-center': [
					{
						name: 'User Profile',
						path: 'user-profile',
					},
					{
						name: 'Company Profile',
						path: 'company-profile',
					},
					{
						name: 'Disclaimers',
						path: 'disclaimers',
					},
					{
						name: 'Alerts',
						path: 'alerts',
						hidden: this.userInPilot,
					},
					{
						name: 'Mortgage Matchup',
						path: 'mortgagematchup',
						hidden: !this.hasFambFeature || this.userInPilot,
					},
					{
						name: 'Permissions',
						path: 'permissions',
						hidden: !this.showPermissionsTab || this.userInPilot,
					},
				],
				analytics: [
					{
						name: 'Monthly Hits',
						path: 'monthly-hits',
					},
					{
						name: 'Year to Date',
						path: 'year-to-date',
					},
				],
			};
		},
	},
	watch: {
		$route() {
			this.updateSubnav();
		},
		hasFambFeature() {
			this.updateSubnav();
		},
		showPermissionsTab() {
			this.updateSubnav();
		},
	},
	methods: {
		/* Selects the correct subnav based on the first item in the current route */
		updateSubnav() {
			const currentPathArray = this.$router.history.current.path
				.split('/')
				.filter((el) => el !== '');
			this.secondaryNavigationList =
				currentPathArray.length === 0
					? []
					: this.secondaryNavigationRoutes[currentPathArray[0]];
			// If we are on a subroute's child route (ie. /client-connect-v2/drip-campaigns/campaign/3)
			// then get relative level so it can be appended to the secondary navs paths
			this.pathRelativeLevel = currentPathArray.reduce(
				(pathLevel, pathItem, index) => {
					return index > 1 ? pathLevel + '../' : '';
				},
				'',
			);
		},
	},
	created() {
		this.updateSubnav();
	},
};
</script>

<style scoped lang="scss">
.expanded {
	@include lg {
		width: $expanded-menu-width !important;
	}
	@include sm {
		display: none;
	}
}

.lg-context-nav {
	position: fixed;
	left: 0;
	z-index: 9;
	width: 100%;
	height: $context-nav-height;
	background-color: $white;
	border-bottom: $border-width solid $border-color-light;
	box-sizing: border-box;
	display: flex;
	align-items: center;
	justify-content: flex-start;
	overflow-x: scroll;
	scrollbar-width: none;
	scroll-snap-type: y mandatory;

	&::-webkit-scrollbar {
		height: 0 !important;
		width: 0 !important;
	}
	a {
		@include rem('padding', 19.5px $base-padding);
		text-decoration: none;
		color: $text-base-color;
		position: relative;
		scroll-snap-align: start;

		&:hover {
			color: $brand-blue;
		}
		&:focus,
		&.router-link-active {
			color: $brand-blue;

			&:after {
				content: '';
				position: absolute;
				width: 100%;
				height: 2px;
				left: 0;
				bottom: 0;
				background: $brand-blue;
			}
		}

		&.disabled:hover {
			background: $b360-color--gray__header-bg;
		}
		&.disabled {
			color: $b360-color--gray__medium;
			cursor: not-allowed;
			pointer-events: none;
		}
	}

	.active {
		color: $brand-blue;

		&:after {
			content: '';
			width: 100%;
			position: absolute;
			left: 0;
			bottom: 0;
			border-width: 0 0 ($border-width * 2);
			border-style: solid;
		}
	}

	@include lg {
		top: $top-nav-height + $border-width;
		left: $collapsed-menu-width;
		overflow-x: unset;
	}
}

.expandedContextNav {
	@include lg {
		left: $expanded-menu-width + $border-width;
	}
}
</style>
